import { useState, useEffect } from 'react'
import useDataStaleness from '../hooks/useDataStaleness'
import { calculateDataSourceHealth } from '../../../shared/utils/dataSourceHealth'

const severityConfig = {
  critical: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    icon: 'text-red-500',
    badge: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400',
    label: 'Critical'
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    icon: 'text-amber-500',
    badge: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400',
    label: 'Warning'
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    icon: 'text-blue-500',
    badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400',
    label: 'Info'
  }
}

function SeverityIcon({ severity }) {
  if (severity === 'critical') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    )
  }
  if (severity === 'warning') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function AlertDetails({ alert }) {
  if (!alert.details && !alert.action) return null
  return (
    <div className="mt-3 space-y-2">
      {alert.action && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Action:</span>
          <code className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-0.5 rounded">{alert.action}</code>
        </div>
      )}
      {alert.file && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">File:</span>
          <code className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-0.5 rounded">{alert.file}</code>
        </div>
      )}
      {alert.details && (
        <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{alert.details}</p>
      )}
      {alert.collectFields && alert.collectFields.length > 0 && (
        <div className="mt-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Fields to collect:</span>
          <ul className="mt-1 space-y-0.5">
            {alert.collectFields.map((field, i) => (
              <li key={i} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-1.5">
                <span className="text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0">&bull;</span>
                {field}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default function GlobalNotifications() {
  const { alerts } = useDataStaleness()
  const [dataHealth, setDataHealth] = useState(null)
  const [showCurrent, setShowCurrent] = useState(false)

  useEffect(() => {
    setDataHealth(calculateDataSourceHealth())
  }, [])

  const actionableAlerts = alerts.filter(a => a.severity !== 'info')
  const infoAlerts = alerts.filter(a => a.severity === 'info')

  const statusBadge = {
    overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    due: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    'not-ingested': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    current: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
  }
  const priorityBadge = {
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    medium: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  }
  const categoryLabels = {
    'claims-financial': 'Claims & Financial',
    'threat-intelligence': 'Threat Intelligence',
    'maturity-benchmark': 'Maturity Benchmark'
  }

  const groupedSources = dataHealth ? {
    overdue: dataHealth.sources.filter(s => s.calculatedStatus === 'overdue'),
    due: dataHealth.sources.filter(s => s.calculatedStatus === 'due'),
    upcoming: dataHealth.sources.filter(s => s.calculatedStatus === 'upcoming'),
    'not-ingested': dataHealth.sources.filter(s => s.calculatedStatus === 'not-ingested'),
    current: dataHealth.sources.filter(s => s.calculatedStatus === 'current')
  } : {}

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Notifications</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Data freshness alerts and reference data source status.
        </p>
      </div>

      {/* ── Data Source Updates ── */}
      {dataHealth && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/50">
            <div className="flex items-center gap-2.5">
              <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375" />
              </svg>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Data Source Updates</h2>
            </div>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              {dataHealth.summary.total} sources
            </span>
          </div>

          <div className="px-5 py-4 space-y-5">
            {/* Groups */}
            {[
              { key: 'overdue', label: 'Action Required', items: groupedSources.overdue },
              { key: 'due', label: 'Review Needed', items: groupedSources.due },
              { key: 'upcoming', label: 'Updates Expected Soon', items: groupedSources.upcoming },
              { key: 'not-ingested', label: 'Not Yet Integrated', items: groupedSources['not-ingested'] }
            ].map(group => {
              if (!group.items?.length) return null
              return (
                <div key={group.key}>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{group.label}</p>
                  <div className="space-y-2">
                    {group.items.map(s => (
                      <div key={s.id} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge[s.calculatedStatus]}`}>{s.statusInfo?.label}</span>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{s.name}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${priorityBadge[s.priority] || priorityBadge.medium}`}>{s.priority}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">{categoryLabels[s.category] || s.category}</span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {s.publisher} &middot; {s.refreshCadence}
                            </p>
                            <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                              <span>Last ingested: {s.lastIngested ? `${new Date(s.lastIngested).toLocaleDateString()} (${s.lastIngestedVersion || ''})` : 'Never'}</span>
                              {s.nextExpectedDate && (
                                <span>Next expected: {s.nextExpectedDate}{s.daysUntilExpected != null ? (s.daysUntilExpected < 0 ? ` (${Math.abs(s.daysUntilExpected)} days overdue)` : ` (${s.daysUntilExpected} days)`) : ''}</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Feeds: {(s.feedsModules || []).join(', ')}</p>
                          </div>
                          {s.checkUrl && (
                            <a href={s.checkUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs flex items-center gap-1 flex-shrink-0">
                              Check
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}

            {/* Current — collapsed by default */}
            {groupedSources.current?.length > 0 && (
              <div>
                <button onClick={() => setShowCurrent(!showCurrent)} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                  <svg className={`w-3 h-3 transition-transform ${showCurrent ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="font-semibold uppercase tracking-wider">Current ({groupedSources.current.length})</span>
                </button>
                {showCurrent && (
                  <div className="space-y-2 mt-2">
                    {groupedSources.current.map(s => (
                      <div key={s.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge.current}`}>{s.statusInfo?.label}</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{s.name}</span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {s.publisher} &middot; Ingested {s.lastIngested ? new Date(s.lastIngested).toLocaleDateString() : 'N/A'}
                            {s.daysSinceIngested != null ? ` (${s.daysSinceIngested} days ago)` : ''}
                          </p>
                        </div>
                        {s.checkUrl && (
                          <a href={s.checkUrl} target="_blank" rel="noopener noreferrer" className="text-gray-300 dark:text-gray-600 hover:text-primary-500 dark:hover:text-primary-400 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Existing Alerts ── */}
      {actionableAlerts.length === 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 text-center">
          <svg className="w-8 h-8 text-green-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-green-800 dark:text-green-300 font-medium">All data sources are up to date</p>
        </div>
      )}

      {actionableAlerts.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Action Required</h2>
          {actionableAlerts.map(alert => {
            const config = severityConfig[alert.severity]
            return (
              <div key={alert.id} className={`${config.bg} border ${config.border} rounded-xl p-4 flex items-start gap-4`}>
                <div className={`${config.icon} mt-0.5 flex-shrink-0`}>
                  <SeverityIcon severity={alert.severity} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900 dark:text-white text-sm">{alert.source}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.badge}`}>{config.label}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{alert.message}</p>
                  {alert.lastUpdated && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Last updated: {new Date(alert.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                  <AlertDetails alert={alert} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {infoAlerts.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Informational</h2>
          {infoAlerts.map(alert => {
            const config = severityConfig[alert.severity]
            return (
              <div key={alert.id} className={`${config.bg} border ${config.border} rounded-xl p-4 flex items-start gap-4`}>
                <div className={`${config.icon} mt-0.5 flex-shrink-0`}>
                  <SeverityIcon severity={alert.severity} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900 dark:text-white text-sm">{alert.source}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.badge}`}>{config.label}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{alert.message}</p>
                  <AlertDetails alert={alert} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Data Source Schedule</h2>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-gray-600 dark:text-gray-400">Regulatory Updates</span>
            <span className="text-gray-900 dark:text-white font-medium">Weekly (automated script)</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-gray-600 dark:text-gray-400">Industry Benchmarks</span>
            <span className="text-gray-900 dark:text-white font-medium">Semi-annually (manual review)</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-gray-600 dark:text-gray-400">Threat Profiles</span>
            <span className="text-gray-900 dark:text-white font-medium">Quarterly (automated script — CISA KEV + MITRE ATT&CK)</span>
          </div>
        </div>
      </div>
    </div>
  )
}
